/**
 * ReportToolbar — SSRS-style pagination + export toolbar.
 *
 * Composes: first / prev / page-input / "of N" / next / last
 * Plus: refresh, zoom −/+ (75/100/125/150%), CSV, PDF.
 *
 * Designed to wrap any server-paginated list:
 *   <ReportToolbar
 *     page={page} totalPages={totalPages} total={total} pageSize={pageSize}
 *     onPageChange={setPage} onRefresh={load}
 *     zoom={zoom} onZoomChange={setZoom}
 *     onExportCsv={exportCsv} onExportPdf={exportPdf}
 *   />
 *
 * Latest data first comes from the server (sort: created_at desc).
 */
import { CaretLeft, CaretRight, CaretDoubleLeft, CaretDoubleRight, ArrowsClockwise, MinusCircle, PlusCircle, DownloadSimple, Printer } from "@phosphor-icons/react";
import { useEffect, useState } from "react";

const ZOOM_STEPS = [75, 100, 125, 150];
export const ZOOM_DEFAULT = 100;

export default function ReportToolbar({
    page = 1,
    totalPages = 1,
    total = 0,
    pageSize = 20,
    onPageChange,
    onRefresh,
    zoom = ZOOM_DEFAULT,
    onZoomChange,
    onExportCsv,
    onExportPdf,
    busy = false,
    leftSlot,
}) {
    const [draft, setDraft] = useState(String(page));
    useEffect(() => { setDraft(String(page)); }, [page]);

    const go = (n) => {
        const next = Math.min(Math.max(1, n), Math.max(1, totalPages));
        // Always re-sync the visible input to the (possibly clamped) target so
        // typing an out-of-range page snaps back even when it equals the
        // currently-active page (e.g. typing "2" with totalPages=1).
        setDraft(String(next));
        if (next !== page) onPageChange?.(next);
    };
    const commitDraft = () => {
        const n = parseInt(draft, 10);
        if (Number.isFinite(n)) go(n); else setDraft(String(page));
    };
    const zoomDown = () => {
        const idx = ZOOM_STEPS.indexOf(zoom);
        const next = idx > 0 ? ZOOM_STEPS[idx - 1] : ZOOM_STEPS[0];
        onZoomChange?.(next);
    };
    const zoomUp = () => {
        const idx = ZOOM_STEPS.indexOf(zoom);
        const next = idx >= 0 && idx < ZOOM_STEPS.length - 1
            ? ZOOM_STEPS[idx + 1]
            : ZOOM_STEPS[ZOOM_STEPS.length - 1];
        onZoomChange?.(next);
    };

    const first = page <= 1;
    const last = page >= totalPages;
    const rangeStart = total === 0 ? 0 : (page - 1) * pageSize + 1;
    const rangeEnd = Math.min(page * pageSize, total);

    const Btn = ({ onClick, disabled, children, title, testid }) => (
        <button
            type="button"
            onClick={onClick}
            disabled={disabled || busy}
            title={title}
            data-testid={testid}
            className="p-1.5 rounded text-fleet-ink hover:bg-slate-100 disabled:opacity-40 disabled:cursor-not-allowed transition-colors"
        >
            {children}
        </button>
    );

    return (
        <div className="flex items-center gap-1 flex-wrap text-xs border border-fleet-border rounded-md bg-slate-50 px-2 py-1.5" data-testid="report-toolbar">
            {leftSlot}
            <Btn onClick={() => go(1)} disabled={first} title="First page" testid="rt-first">
                <CaretDoubleLeft size={14} weight="bold" />
            </Btn>
            <Btn onClick={() => go(page - 1)} disabled={first} title="Previous page" testid="rt-prev">
                <CaretLeft size={14} weight="bold" />
            </Btn>
            <input
                type="text"
                inputMode="numeric"
                value={draft}
                onChange={(e) => setDraft(e.target.value.replace(/\D/g, ""))}
                onBlur={commitDraft}
                onKeyDown={(e) => { if (e.key === "Enter") commitDraft(); }}
                className="w-12 text-center font-mono border border-fleet-border rounded bg-white text-xs py-0.5"
                data-testid="rt-page-input"
                aria-label="Page number"
            />
            <span className="text-fleet-inkSoft font-mono px-1">of {totalPages}</span>
            <Btn onClick={() => go(page + 1)} disabled={last} title="Next page" testid="rt-next">
                <CaretRight size={14} weight="bold" />
            </Btn>
            <Btn onClick={() => go(totalPages)} disabled={last} title="Last page" testid="rt-last">
                <CaretDoubleRight size={14} weight="bold" />
            </Btn>

            <div className="w-px h-5 bg-fleet-border mx-1" />

            {onRefresh && (
                <Btn onClick={onRefresh} disabled={false} title="Refresh" testid="rt-refresh">
                    <ArrowsClockwise size={14} weight="bold" className={busy ? "animate-spin" : ""} />
                </Btn>
            )}

            <div className="w-px h-5 bg-fleet-border mx-1" />

            <Btn onClick={zoomDown} disabled={zoom <= ZOOM_STEPS[0]} title="Zoom out" testid="rt-zoom-out">
                <MinusCircle size={14} weight="bold" />
            </Btn>
            <span className="font-mono text-fleet-inkSoft px-1 min-w-[3rem] text-center" data-testid="rt-zoom">{zoom}%</span>
            <Btn onClick={zoomUp} disabled={zoom >= ZOOM_STEPS[ZOOM_STEPS.length - 1]} title="Zoom in" testid="rt-zoom-in">
                <PlusCircle size={14} weight="bold" />
            </Btn>

            <div className="w-px h-5 bg-fleet-border mx-1" />

            {onExportCsv && (
                <button
                    type="button"
                    onClick={onExportCsv}
                    disabled={busy}
                    title="Export CSV (all rows matching current filters)"
                    data-testid="rt-csv"
                    className="px-2 py-1 rounded text-xs font-medium text-fleet-ink hover:bg-slate-100 flex items-center gap-1 disabled:opacity-40"
                >
                    <DownloadSimple size={12} /> CSV
                </button>
            )}
            {onExportPdf && (
                <button
                    type="button"
                    onClick={onExportPdf}
                    disabled={busy}
                    title="Print / save as PDF (all rows matching current filters)"
                    data-testid="rt-pdf"
                    className="px-2 py-1 rounded text-xs font-medium text-fleet-ink hover:bg-slate-100 flex items-center gap-1 disabled:opacity-40"
                >
                    <Printer size={12} /> PDF
                </button>
            )}

            <span className="ml-auto pl-2 pr-1 text-fleet-inkSoft font-mono" data-testid="rt-range">
                {total === 0 ? "0 rows" : `${rangeStart}–${rangeEnd} of ${total}`}
            </span>
        </div>
    );
}

/**
 * Convenience helper that wraps children with a CSS zoom level set by the
 * toolbar. Use it around the table you want to scale.
 */
export function ZoomScope({ zoom = ZOOM_DEFAULT, children }) {
    return (
        <div style={{ zoom: `${zoom}%` }} data-testid="zoom-scope">{children}</div>
    );
}
